<template>
  <div class="page">
    <div class="header">
      <button class="back-btn" @click="router.push('/')">
        &larr; 返回首页
      </button>
      <h1 class="page-title">接单中心</h1>
    </div>
    <div class="content">
      <!-- 未登录状态 -->
      <div v-if="!isLoggedIn" class="empty-state">
        <Lock size="48" class="empty-icon" />
        <h2>请先登录</h2>
        <p>登录后才能查看和接取订单</p>
        <button class="login-btn" @click="router.push('/mypage')">去登录</button>
      </div>

      <!-- 加载状态 -->
      <div v-else-if="loading" class="loading-state">
        <div class="spinner"></div>
        <span>加载中...</span>
      </div>

      <!-- 订单列表 -->
      <div v-else-if="orders.length > 0" class="order-list">
        <!-- 热刷新状态栏 -->
        <div class="refresh-bar">
          <div class="refresh-info">
            <span class="refresh-dot" :class="{ active: autoRefreshEnabled }"></span>
            <span class="refresh-text">
              {{ autoRefreshEnabled ? '每3秒自动刷新' : '自动刷新已暂停' }}
            </span>
          </div>
          <div class="refresh-actions">
            <button 
              class="refresh-btn" 
              :class="{ refreshing: isRefreshing }"
              @click="manualRefresh" 
              :disabled="isRefreshing"
            >
              <span v-if="!isRefreshing">手动刷新</span>
              <span v-else>刷新中...</span>
            </button>
            <button 
              class="toggle-btn" 
              :class="{ active: autoRefreshEnabled }"
              @click="toggleAutoRefresh"
            >
              {{ autoRefreshEnabled ? '暂停' : '开启' }}
            </button>
          </div>
        </div>
        <div class="order-card" v-for="order in orders" :key="order.id">
          <div class="order-header">
            <h3 class="order-title">{{ order.title }}</h3>
            <span class="order-price">¥{{ order.price }}</span>
          </div>
          <div class="order-body">
            <div class="order-info">
              <span class="info-label">创建时间:</span>
              <span class="info-value">{{ formatTime(order.created_at) }}</span>
            </div>
            <div class="order-info">
              <span class="info-label">商品内容:</span>
              <span class="info-value">{{ order.desc || '无' }}</span>
            </div>
            <div class="order-info">
              <span class="info-label">游戏ID:</span>
              <span class="game-id-wrapper">
                <span class="info-value game-id">{{ order.game_id || '-' }}</span>
                <button 
                  v-if="order.game_id"
                  class="copy-btn" 
                  @click.stop="copyGameId(order.game_id)"
                  :class="{ copied: copiedId === order.game_id }"
                  title="点击复制游戏ID"
                >
                  <span v-if="copiedId !== order.game_id">复制</span>
                  <span v-else>已复制</span>
                </button>
              </span>
            </div>
            <div class="order-info" v-if="order.mark">
              <span class="info-label">备注:</span>
              <span class="info-value remark">{{ order.mark }}</span>
            </div>
            <div class="order-info" v-if="order.handler_id || order.player">
              <span class="info-label">接单打手:</span>
              <span class="info-value">{{ order.handler_id || order.player }}</span>
            </div>
          </div>
          <div class="order-footer">
            <button 
              v-if="order.status === 'pending'" 
              class="order-btn accept-btn"
              @click="acceptOrder(order.id)"
              :disabled="processingOrders.has(order.id)"
            >
              <span v-if="processingOrders.has(order.id)">处理中...</span>
              <span v-else>接单</span>
            </button>
            <button 
              v-else 
              class="order-btn disabled-btn"
              disabled
            >
              {{ getStatusText(order.status) }}
            </button>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else class="empty-state">
        <Zap size="48" class="empty-icon" />
        <h2>暂无订单</h2>
        <p>当前没有可接取的订单</p>
      </div>
    </div>

    <!-- 弹窗组件 -->
    <Dialog
      v-model:visible="dialogVisible"
      :title="dialogTitle"
      :message="dialogMessage"
      :type="dialogType"
      @close="closeDialog"
    />

    <!-- 搭子输入弹窗 -->
    <div v-if="partnerDialogVisible" class="partner-overlay" @click="closePartnerDialog">
      <div class="partner-dialog" @click.stop>
        <div class="partner-dialog-header">
          <h3>接单成功</h3>
          <button class="close-btn" @click="closePartnerDialog">&times;</button>
        </div>
        <div class="partner-dialog-body">
          <p>请输入接单搭子的ID（选填）</p>
          <input
            v-model="partnerInput"
            type="text"
            class="partner-input"
            placeholder="例如：starry_001"
            @keyup.enter="confirmPartner"
          />
        </div>
        <div class="partner-dialog-footer">
          <button class="btn btn-secondary" @click="closePartnerDialog">跳过</button>
          <button class="btn btn-primary" @click="confirmPartner">确认添加</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { Zap, Lock } from 'lucide-vue-next'
import { supabase } from '@/utils/supabase'
import Dialog from '@/components/Dialog.vue'
import { getSecureStorage, SECURE_STORAGE_KEY, PLATFORM_TABLE_MAP } from '@/utils/security'
import { getFines, getDeposit } from '@/api/orders'
import { chatManager } from '@/utils/chatManager'

const router = useRouter()

// 登录状态
const isLoggedIn = ref(false)
const currentUser = ref(null)

// 订单数据
const loading = ref(true)
const orders = ref([])
const processingOrders = ref(new Set()) // 用于跟踪正在处理的订单，防止重复点击

// 弹窗状态
const dialogVisible = ref(false)
const dialogMessage = ref('')
const dialogType = ref('info')
const dialogTitle = ref('提示')

// 搭子输入弹窗状态
const partnerDialogVisible = ref(false)
const partnerInput = ref('')
const currentOrderId = ref('')

// 复制状态
const copiedId = ref('')

// 自动刷新定时器
const refreshTimer = ref(null)

// 自动刷新开关
const autoRefreshEnabled = ref(true)

// 手动刷新中
const isRefreshing = ref(false)

// 魔王打手标识
const isDemon = ref(false)

// 显示弹窗
const showDialog = (message, type = 'info', title = '提示') => {
  dialogMessage.value = message
  dialogType.value = type
  dialogTitle.value = title
  dialogVisible.value = true
}

// 关闭弹窗
const closeDialog = () => {
  dialogVisible.value = false
}

// 显示搭子输入弹窗
const showPartnerDialog = (orderId) => {
  currentOrderId.value = orderId
  partnerInput.value = ''
  partnerDialogVisible.value = true
}

// 关闭搭子输入弹窗
const closePartnerDialog = () => {
  partnerDialogVisible.value = false
  partnerInput.value = ''
  currentOrderId.value = ''
}

// 确认添加搭子
const confirmPartner = async () => {
  if (!partnerInput.value.trim()) {
    showDialog('请输入搭子ID', 'error')
    return
  }

  try {
    const orderIdStr = String(currentOrderId.value)
    const isXianyuOrder = orderIdStr.startsWith('xianyu_')
    const actualOrderId = isXianyuOrder ? orderIdStr.replace('xianyu_', '') : currentOrderId.value
    const platform = currentUser.value.platform || 'mobile_delta'
    const tables = PLATFORM_TABLE_MAP[platform]
    const tableName = isXianyuOrder ? tables.xianyu_order : tables.orders

    const { error } = await supabase
      .from(tableName)
      .update({ handler_mark: partnerInput.value.trim() })
      .eq('id', actualOrderId)

    if (error) {
      console.error('添加搭子失败:', error)
      showDialog('添加搭子失败: ' + error.message, 'error')
      return
    }

    showDialog('搭子添加成功', 'success')
    closePartnerDialog()
  } catch (error) {
    console.error('添加搭子失败:', error)
    showDialog('添加搭子失败: ' + error.message, 'error')
  }
}

// 复制游戏ID
async function copyGameId(gameId) {
  try {
    await navigator.clipboard.writeText(gameId)
    copiedId.value = gameId
    showDialog('游戏ID已复制到剪贴板', 'success')
    setTimeout(() => {
      copiedId.value = ''
    }, 2000)
  } catch (err) {
    console.error('复制失败:', err)
    showDialog('复制失败，请手动复制', 'error')
  }
}

// 格式化时间
function formatTime(timeStr) {
  if (!timeStr) return '-'  
  const date = new Date(timeStr)
  return date.toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 获取状态文本
function getStatusText(status) {
  const statusMap = {
    '待接': '待接单',
    '待接单': '待接单',
    'pending': '待接单',
    '接取中': '接取中',
    'processing': '接取中',
    'accepted': '接取中',
    '已接单': '接取中',
    'pending_review': '待审核',
    '待审核': '待审核',
    '结单': '已结单',
    'completed': '已结单',
    'finished': '已结单',
    '已完成': '已结单',
    'cancelled': '已取消',
    'transferred': '已转单'
  }
  return statusMap[status] || status
}

// 检查是否有未缴纳的罚款
async function hasUnpaidFines() {
  if (!currentUser.value || !currentUser.value.username) {
    return false
  }

  try {
    const platform = currentUser.value.platform || 'mobile_delta'
    const { data: finesData, error: finesError } = await getFines(currentUser.value.username, platform)
    if (finesError) {
      console.error('获取罚款记录失败:', finesError)
      return false
    }

    return finesData && finesData.some(fine => !fine.paid)
  } catch (error) {
    console.error('检查罚款记录失败:', error)
    return false
  }
}

// 检查是否有押金（50元）
async function hasDeposit() {
  if (!currentUser.value || !currentUser.value.username) {
    return false
  }

  try {
    const platform = currentUser.value.platform || 'mobile_delta'
    const { data: depositData, error: depositError } = await getDeposit(currentUser.value.username, platform)
    if (depositError) {
      console.error('获取押金记录失败:', depositError)
      return false
    }

    if (depositData && depositData.length > 0) {
      const depositAmount = Number(depositData[0].current) || 0
      return depositAmount >= 50
    }
    return false
  } catch (error) {
    console.error('检查押金失败:', error)
    return false
  }
}

// 检查当前已接订单数量
async function getAcceptedOrderCount() {
  if (!currentUser.value || !currentUser.value.username) {
    return 0
  }

  try {
    const platform = currentUser.value.platform || 'mobile_delta'
    const tables = PLATFORM_TABLE_MAP[platform]

    const [ordersResult, xianyuResult] = await Promise.all([
      supabase
        .from(tables.orders)
        .select('id')
        .eq('handler_id', currentUser.value.username)
        .eq('status', 'processing'),
      supabase
        .from(tables.xianyu_order)
        .select('id')
        .eq('player', currentUser.value.username)
        .eq('status', 'processing')
    ])

    const ordersCount = ordersResult.data ? ordersResult.data.length : 0
    const xianyuCount = xianyuResult.data ? xianyuResult.data.length : 0

    return ordersCount + xianyuCount
  } catch (error) {
    console.error('检查已接订单数量失败:', error)
    return 0
  }
}

// 接受订单
async function acceptOrder(orderId) {
  // 防止重复点击
  if (processingOrders.value.has(orderId)) {
    return
  }

  // 检查是否有未缴纳的罚款
  const unpaid = await hasUnpaidFines()
  if (unpaid) {
    showDialog('您有未缴纳的罚款，请先缴纳罚款后再接单', 'error')
    return
  }

  // 检查是否有押金（50元）
  const hasDepositAmount = await hasDeposit()
  if (!hasDepositAmount) {
    showDialog('您尚未缴纳押金，请联系管理员进行押金缴纳后再进行接单', 'error')
    return
  }

  // 检查当前已接订单数量（最多接2单）
  const acceptedCount = await getAcceptedOrderCount()
  if (acceptedCount >= 2) {
    showDialog('您当前已接订单数量已达上限（最多2单），请完成现有订单后再接新单', 'error')
    return
  }

  processingOrders.value.add(orderId)
  try {
    const orderIdStr = String(orderId)
    const isXianyuOrder = orderIdStr.startsWith('xianyu_')
    const actualOrderId = isXianyuOrder ? orderIdStr.replace('xianyu_', '') : orderId
    const platform = currentUser.value.platform || 'mobile_delta'
    const tables = PLATFORM_TABLE_MAP[platform]
    const tableName = isXianyuOrder ? tables.xianyu_order : tables.orders

    const updateData = isXianyuOrder 
      ? { status: 'processing', player: currentUser.value.username }
      : { status: 'processing', handler_id: currentUser.value.username }

    const { error, data } = await supabase
      .from(tableName)
      .update(updateData)
      .eq('id', actualOrderId)
      .eq('status', 'pending')
      .select()

    if (error) {
      console.error('接单失败:', error)
      showDialog('接单失败: ' + error.message, 'error')
      return
    }

    if (!data || data.length === 0) {
      console.error('接单失败: 订单已被其他打手接走')
      showDialog('接单失败，订单已被其他打手接走', 'error')
      return
    }

    // 接单成功后显示搭子输入弹窗
    showPartnerDialog(orderId)
    
    // 接单成功后立即订阅该订单的聊天频道，无需等待打开聊天窗口
    console.log(`[Hold] 接单成功，订阅订单 ${orderId} 的聊天频道`)
    chatManager.init(supabase)
    chatManager.subscribe(orderId, null)
    
    await fetchOrders()
  } catch (error) {
    console.error('接单失败:', error)
    showDialog('接单失败: ' + error.message, 'error')
  } finally {
    processingOrders.value.delete(orderId)
  }
}

// 获取订单列表
async function fetchOrders(isBackgroundRefresh = false) {
  if (!isBackgroundRefresh) {
    loading.value = true
  }
  
  try {
    const platform = currentUser.value?.platform || 'mobile_delta'
    const tables = PLATFORM_TABLE_MAP[platform]

    const [ordersResult, xianyuResult] = await Promise.all([
      supabase
        .from(tables.orders)
        .select('*')
        .order('created_at', { ascending: false }),
      supabase
        .from(tables.xianyu_order)
        .select('*')
        .order('created_at', { ascending: false })
    ])

    const ordersError = ordersResult.error
    const xianyuError = xianyuResult.error

    if (ordersError) console.error('获取orders订单失败:', ordersError)
    if (xianyuError) console.error('获取xianyu_order订单失败:', xianyuError)

    const ordersData = ordersResult.data || []
    const xianyuData = xianyuResult.data || []

    const convertedOrders = ordersData.map(order => ({
      ...order,
      desc: order.content || order.desc || '无商品信息',
      handler_id: order.handler_id || ''
    }))

    const convertedXianyuOrders = xianyuData.map(order => ({
      id: 'xianyu_' + order.id,
      title: order.title || '闲鱼订单',
      price: order.price || 0,
      desc: order.content || order.shop_name || '无商品信息',
      game_id: order.game_id || '',
      user_id: order.player || '',
      status: order.status,
      created_at: order.created_at,
      goods_id: null,
      table_source: 'xianyu_order',
      mark: order.mark || '',
      player: order.player || ''
    }))

    const allOrders = [...convertedOrders, ...convertedXianyuOrders]
    
    allOrders.sort((a, b) => {
      // 统一的状态映射表
      const getStatusPriority = (status) => {
        const pendingStatuses = ['pending', '待接', '待接单', 'new', '未接单']
        if (pendingStatuses.includes(status)) return 0 // 未接单 - 最优先
        
        const statusMap = {
          'processing': 1, '接取中': 1, 'accepted': 1, '已接单': 1,
          'pending_review': 2, '待审核': 2,
          'completed': 3, 'finished': 3, '结单': 3, '已完成': 3,
          'cancelled': 4, 'transferred': 4
        }
        return statusMap[status] ?? 4
      }
      
      const priorityA = getStatusPriority(a.status)
      const priorityB = getStatusPriority(b.status)
      
      // 首先按状态优先级排序
      if (priorityA !== priorityB) {
        return priorityA - priorityB
      }
      
      // 同一状态按派单时间倒序（越晚越靠前）
      const timeA = new Date(a.created_at).getTime()
      const timeB = new Date(b.created_at).getTime()
      return timeB - timeA
    })

    // 非魔王打手：未接取订单需要延迟10秒才能看到
    const pendingStatuses = ['pending', '待接', '待接单', 'new', '未接单']
    let filteredOrders = allOrders
    if (!isDemon.value) {
      const now = Date.now()
      const DELAY_MS = 10 * 1000 // 10秒延迟
      filteredOrders = allOrders.filter(order => {
        // 未接取的订单需要检查创建时间
        if (pendingStatuses.includes(order.status)) {
          const createdAt = new Date(order.created_at).getTime()
          const elapsed = now - createdAt
          if (elapsed < DELAY_MS) {
            return false // 未超过10秒，隐藏
          }
        }
        return true
      })
      const hiddenCount = allOrders.length - filteredOrders.length
      if (hiddenCount > 0 && !isBackgroundRefresh) {
        console.log(`[Hold] 普通打手隐藏了 ${hiddenCount} 个未满10秒的新订单`)
      }
    }
    
    const recentOrders = filteredOrders.slice(0, 20)
    
    if (recentOrders.length > 0) {
      const startTime = Date.now()
      
      const nonXianyuOrders = recentOrders.filter(o => o.table_source !== 'xianyu_order')
      const goodsIds = [...new Set(nonXianyuOrders.filter(o => o.goods_id).map(o => o.goods_id))]
      
      let goodsMap = {}
      if (goodsIds.length > 0) {
        const { data: goodsData, error: goodsError } = await supabase
          .from('goods')
          .select('id, desc')
          .in('id', goodsIds)
        
        if (goodsError) {
          console.error('批量查询商品信息失败:', goodsError)
        } else if (goodsData) {
          goodsMap = goodsData.reduce((map, goods) => {
            map[goods.id] = goods.desc
            return map
          }, {})
        }
      }
      
      const ordersWithGoods = recentOrders.map(order => {
        if (order.table_source === 'xianyu_order') {
          return order
        }
        
        const goodsDesc = goodsMap[order.goods_id]
        if (goodsDesc) {
          return { ...order, desc: goodsDesc }
        }
        
        return { ...order, desc: order.desc || '无商品信息' }
      })
      
      const fetchDuration = Date.now() - startTime
      console.log(`[Hold] 订单列表刷新完成，耗时: ${fetchDuration}ms，订单数: ${recentOrders.length}`)
      
      // 比较新旧数据，只有数据变化时才更新
      const newOrdersJson = JSON.stringify(ordersWithGoods.map(o => ({ id: o.id, status: o.status, price: o.price })))
      const oldOrdersJson = JSON.stringify(orders.value.map(o => ({ id: o.id, status: o.status, price: o.price })))
      
      if (newOrdersJson !== oldOrdersJson) {
        orders.value = ordersWithGoods
      }
    } else {
      // 如果新数据为空，但旧数据不为空，才更新
      if (orders.value.length > 0) {
        orders.value = []
      }
    }
  } catch (error) {
    console.error('获取订单失败:', error)
    // 只在首次加载时显示错误提示，后台刷新静默失败
    if (!isBackgroundRefresh) {
      showDialog('获取订单失败: ' + error.message, 'error')
    }
  } finally {
    if (!isBackgroundRefresh) {
      loading.value = false
    }
  }
}

// 获取打手的魔王身份
async function fetchDemonStatus() {
  if (!currentUser.value || !currentUser.value.username) {
    isDemon.value = false
    return
  }

  try {
    const platform = currentUser.value.platform || 'mobile_delta'
    const tables = PLATFORM_TABLE_MAP[platform]

    const { data, error } = await supabase
      .from(tables.player)
      .select('is_demon')
      .eq('username', currentUser.value.username)
      .limit(1)

    if (error) {
      console.error('获取魔王身份失败:', error)
      isDemon.value = false
      return
    }

    if (data && data.length > 0) {
      isDemon.value = data[0].is_demon === true
      console.log(`[Hold] 打手身份: ${isDemon.value ? '魔王' : '普通打手'}`)
    } else {
      isDemon.value = false
    }
  } catch (error) {
    console.error('获取魔王身份异常:', error)
    isDemon.value = false
  }
}

// 检查登录状态
function checkLoginStatus() {
  const savedUser = getSecureStorage(SECURE_STORAGE_KEY)
  if (savedUser) {
    isLoggedIn.value = true
    currentUser.value = savedUser
    return true
  }
  isLoggedIn.value = false
  return false
}

onMounted(async () => {
  const loggedIn = checkLoginStatus()
  if (loggedIn) {
    await fetchDemonStatus() // 先获取魔王身份
    fetchOrders() // 首次加载
    // 每3秒自动刷新订单列表（热刷新）
    startAutoRefresh()
  }
})

onUnmounted(() => {
  // 清除定时器
  stopAutoRefresh()
})

// 启动自动刷新
function startAutoRefresh() {
  stopAutoRefresh()
  refreshTimer.value = setInterval(() => {
    fetchOrders(true) // 传入 true 表示后台刷新
  }, 3000)
}

// 停止自动刷新
function stopAutoRefresh() {
  if (refreshTimer.value) {
    clearInterval(refreshTimer.value)
    refreshTimer.value = null
  }
}

// 手动刷新
function manualRefresh() {
  if (isRefreshing.value) return
  isRefreshing.value = true
  fetchOrders().then(() => {
    setTimeout(() => {
      isRefreshing.value = false
    }, 500)
  }).catch(() => {
    isRefreshing.value = false
  })
}

// 切换自动刷新状态
function toggleAutoRefresh() {
  autoRefreshEnabled.value = !autoRefreshEnabled.value
  if (autoRefreshEnabled.value) {
    startAutoRefresh()
  } else {
    stopAutoRefresh()
  }
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  padding: 20px;
  padding-top: 60px;
}

.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 56px;
  background: var(--color-bg-card);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  padding: 0 20px;
  z-index: 100;
}

.back-btn {
  background: none;
  border: none;
  color: var(--color-text-primary);
  font-size: 14px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: var(--radius-md);
  transition: var(--transition-fast);
}

.back-btn:hover {
  background: rgba(99, 102, 241, 0.1);
}

.page-title {
  flex: 1;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.content {
  margin-top: 20px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  gap: 16px;
}

.empty-icon {
  color: var(--color-accent-primary);
  opacity: 0.6;
}

.empty-state h2 {
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

.empty-state p {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0;
}

.login-btn {
  margin-top: 8px;
  padding: 10px 24px;
  background: var(--color-accent-gradient);
  color: #fff;
  border: none;
  border-radius: var(--radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-fast);
}

.login-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

/* 加载状态 */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 0;
  gap: 12px;
  color: var(--color-text-muted);
  font-size: 14px;
}

.spinner {
  width: 28px;
  height: 28px;
  border: 3px solid var(--color-border);
  border-top-color: var(--color-accent-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 订单列表 */
.order-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 20px;
}

/* 热刷新状态栏 */
.refresh-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(102, 126, 234, 0.08);
  border: 1px solid rgba(102, 126, 234, 0.15);
  border-radius: 12px;
  margin-bottom: 4px;
}

.refresh-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.refresh-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #94a3b8;
  transition: all 0.3s;
}

.refresh-dot.active {
  background: #10b981;
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.5);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.refresh-text {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

.refresh-actions {
  display: flex;
  gap: 8px;
}

.refresh-btn,
.toggle-btn {
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
}

.refresh-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
}

.refresh-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.refresh-btn.refreshing {
  background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
}

.toggle-btn {
  background: #f1f5f9;
  color: #64748b;
  border-color: #e2e8f0;
}

.toggle-btn.active {
  background: #fef3c7;
  color: #92400e;
  border-color: #fcd34d;
}

.toggle-btn:hover {
  transform: translateY(-1px);
}

.order-card {
  background: var(--color-bg-card);
  border-radius: var(--radius-xl);
  padding: 20px;
  border: 1px solid var(--color-border);
  transition: var(--transition-fast);
}

.order-card:hover {
  border-color: var(--color-accent-primary);
  box-shadow: 0 4px 20px rgba(99, 102, 241, 0.1);
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--color-border);
}

.order-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
  flex: 1;
  margin-right: 12px;
}

.order-price {
  font-size: 18px;
  font-weight: 700;
  color: var(--color-success);
  white-space: nowrap;
}

.order-body {
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.order-info {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

/* 商品内容样式 */
.order-info:nth-child(2) .info-value {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.4;
  max-height: 120px;
  overflow-y: auto;
  flex: 1;
}

.info-label {
  font-size: 13px;
  color: var(--color-text-muted);
  min-width: 70px;
}

.info-value {
  font-size: 13px;
  color: var(--color-text-secondary);
  flex: 1;
}

.game-id-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.game-id {
  font-family: monospace;
  background: var(--color-bg-primary);
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
}

.copy-btn {
  padding: 4px 10px;
  background: var(--color-accent-primary);
  color: #fff;
  border: none;
  border-radius: var(--radius-sm);
  font-size: 12px;
  cursor: pointer;
  transition: var(--transition-fast);
}

.copy-btn:hover {
  background: var(--color-accent-primary-dark);
}

.copy-btn.copied {
  background: var(--color-success);
}

.remark {
  white-space: pre-wrap;
  word-break: break-word;
  color: var(--color-text-secondary);
  background: var(--color-bg-primary);
  padding: 6px 10px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
  flex: 1;
  line-height: 1.4;
}

.status-pending {
  color: var(--color-warning);
  font-weight: 500;
}

.status-accepted {
  color: var(--color-accent-primary);
  font-weight: 500;
}

.status-completed {
  color: var(--color-success);
  font-weight: 500;
}

.status-cancelled {
  color: var(--color-error);
  font-weight: 500;
}

.status-transferred {
  color: var(--color-text-secondary);
  font-weight: 500;
}

.order-footer {
  display: flex;
  justify-content: flex-end;
}

.order-btn {
  padding: 10px 24px;
  border: none;
  border-radius: var(--radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-fast);
}

.accept-btn {
  background: var(--color-accent-gradient);
  color: #fff;
}

.accept-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.disabled-btn {
  background: var(--color-bg-tertiary);
  color: var(--color-text-muted);
  cursor: not-allowed;
}

/* 搭子输入弹窗样式 */
.partner-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.partner-dialog {
  background: var(--color-bg-card);
  border-radius: var(--radius-xl);
  width: 90%;
  max-width: 400px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.partner-dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
}

.partner-dialog-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.partner-dialog-header .close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: var(--color-text-muted);
  cursor: pointer;
  padding: 4px;
  line-height: 1;
}

.partner-dialog-header .close-btn:hover {
  color: var(--color-text-primary);
}

.partner-dialog-body {
  padding: 24px;
}

.partner-dialog-body p {
  margin: 0 0 16px 0;
  font-size: 14px;
  color: var(--color-text-secondary);
}

.partner-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: 14px;
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.partner-input:focus {
  outline: none;
  border-color: var(--color-accent-primary);
}

.partner-input::placeholder {
  color: var(--color-text-muted);
}

.partner-dialog-footer {
  display: flex;
  gap: 12px;
  padding: 16px 24px 24px;
  justify-content: flex-end;
}

.partner-dialog-footer .btn {
  padding: 10px 24px;
  border: none;
  border-radius: var(--radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-fast);
}

.partner-dialog-footer .btn-secondary {
  background: var(--color-bg-tertiary);
  color: var(--color-text-secondary);
}

.partner-dialog-footer .btn-secondary:hover {
  background: var(--color-border);
}

.partner-dialog-footer .btn-primary {
  background: var(--color-accent-gradient);
  color: #fff;
}

.partner-dialog-footer .btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}
</style>